import { cn } from "@newxsystems/shared";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { IDaylightCommonContentProps } from "./DaylightCommonContent.props";

export const DaylightCommonContent = ({
  alignment,
  buttons,
  className,
  description,
  eyebrow,
  heading,
  headingAs = "h2",
  headingClassName,
  tone = "light",
}: IDaylightCommonContentProps) => {
  if (!eyebrow && !heading && !description && !buttons?.length) return null;

  const Heading = headingAs;
  const centered = alignment === "center";
  const actions = buttons;

  return (
    <div
      className={cn(
        "flex max-w-[48rem] flex-col gap-5",
        centered && "mx-auto items-center text-center",
        className,
      )}
    >
      {eyebrow ? (
        <p
          className={cn(
            "flex w-fit items-center gap-3 font-daylight-body text-sm font-semibold leading-[1.4] text-cobalt before:h-px before:w-8 before:bg-current",
            tone === "dark" && "text-mineral-gray",
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      {heading ? (
        <Heading
          className={cn(
            "break-words font-daylight-display text-[clamp(2.25rem,5vw,2.875rem)] font-semibold leading-[1.01] tracking-[-0.035em] text-balance text-foreground",
            tone === "dark" && "text-mineral-gray",
            headingClassName,
          )}
        >
          {heading}
        </Heading>
      ) : null}
      {description ? (
        <DaylightRichText
          className={cn(
            "max-w-[65ch]",
            tone === "dark" &&
              "text-mineral-gray [&_a]:text-mineral-gray [&_blockquote]:border-mineral-gray/40 [&_blockquote]:text-mineral-gray",
          )}
          content={description}
        />
      ) : null}
      {actions?.length ? (
        <div className={cn("flex flex-wrap gap-3 pt-3", centered && "justify-center")}>
          {actions.map((button, index) => (
            <DaylightAction
              action={button}
              className={tone === "dark" ? "border-mineral-gray text-mineral-gray" : undefined}
              key={`${button.href}-${index}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

DaylightCommonContent.displayName = "Content::DaylightCommonContent";
